import { trackEventV2 } from 'tracking/tracking';
import { DataOverviewTypes } from './dataOverview/types';
import { PendingTagChange } from '@ardoq/core';
import {
  InventoryExportFormat,
  PROVENANCE_FIELD_KEYS,
  SortOrder,
  ViewIds,
} from '@ardoq/api-types';
import {
  RuleBasedDeleteData,
  RuleBasedEditFieldData,
  inventoryDatasourceOperations,
  InventoryDatasourceSelection,
  InventoryDatasourceSelectionType,
  ReferenceUpdateSource,
  TAGS_COLUMN,
} from '@ardoq/inventory';

const trackOpenInventoryFromSidebar = () => {
  trackEventV2({ event: 'Inventory0 Opened from sidebar' });
};

const trackOpenInventoryFromDiscoverLandingPage = () => {
  trackEventV2({ event: 'Inventory0 Opened from Discover Landing Page' });
};

const trackOpenInventoryFromAssetManager = ({
  numberOfItems,
}: {
  numberOfItems: number;
}) => {
  trackEventV2({
    event: 'Inventory0 Opened from asset manager',
    payload: { numberOfItems },
  });
};

const trackOpenInventoryFromMetamodel = () => {
  trackEventV2({ event: 'Inventory0 Opened from metamodel generator' });
};

const trackClipboardDataTooLarge = (
  count: number,
  from: InventoryDatasourceSelectionType
) => {
  trackEventV2({
    event: 'Inventory0 Clipboard data too large',
    payload: { count, from },
  });
};

const trackFocusOnSearchInputInDataOverview = (
  dataOverviewType: DataOverviewTypes
) => {
  trackEventV2({
    event: 'Inventory0 Focused on search input in Data Overview',
    payload: { dataOverviewType },
  });
};

const trackClearInSearchInputInDataOverview = () => {
  trackEventV2({ event: 'Inventory0 Cleared search input in Data Overview' });
};

const trackSelectTypeInDataOverview = (dataOverviewType: DataOverviewTypes) => {
  trackEventV2({
    event: 'Inventory0 Selected type in Data Overview',
    payload: { dataOverviewType },
  });
};

const trackOpenItemsInInventory = ({
  dataOverviewType,
  numberOfItems,
}: {
  dataOverviewType: DataOverviewTypes;
  numberOfItems: number;
}) => {
  trackEventV2({
    event: 'Inventory0 Opened items in Inventory',
    payload: { dataOverviewType, numberOfItems },
  });
};

const trackClickOnItemNameInDataOverview = (
  dataOverviewType: DataOverviewTypes
) => {
  trackEventV2({
    event: 'Inventory0 Clicked on item name in Data Overview',
    payload: { dataOverviewType },
  });
};

const trackToggleCheckboxForItemInDataOverview = ({
  dataOverviewType,
  value,
}: {
  dataOverviewType: DataOverviewTypes;
  value: boolean;
}) => {
  trackEventV2({
    event: 'Inventory0 Toggled checkbox for item in Data Overview',
    payload: { dataOverviewType, value },
  });
};

const trackClickOnOpenButtonInDataOverview = ({
  dataOverviewType,
  numberOfItems,
}: {
  dataOverviewType: DataOverviewTypes;
  numberOfItems: number;
}) => {
  trackEventV2({
    event: 'Inventory0 Clicked on Open button in Data Overview',
    payload: { dataOverviewType, numberOfItems },
  });
};

const trackOpenColumnSelectionPanelFromTopRow = () => {
  trackEventV2({
    event: 'Inventory0 Opened column selection panel from Top Row',
  });
};

const trackEnterCreateComponentsModeFromTopRow = () => {
  trackEventV2({
    event: 'Inventory0 Entered create components mode from Top Row',
  });
};

const trackEnterCreateComponentsModeFromEmptyState = () => {
  trackEventV2({
    event: 'Inventory0 Entered create components mode from empty state',
  });
};

const trackExitCreateComponentsModeFromTopRow = () => {
  trackEventV2({
    event: 'Inventory0 Exited create components mode from Top Row',
  });
};

const trackAddNewRowFromTopRow = () => {
  trackEventV2({ event: 'Inventory0 Added new row from Top Row' });
};

const trackDeselectAllRowsFromTopRow = () => {
  trackEventV2({ event: 'Inventory0 Deselected all rows from Top Row' });
};

const trackDeleteSelectedRowsFromTopRow = (numberOfComponents: number) => {
  trackEventV2({
    event: 'Inventory0 Deleted selected components from Top Row',
    payload: { numberOfComponents },
  });
};

const trackNavigateToAuditLogFromTopRow = (numberOfComponents: number) => {
  trackEventV2({
    event: 'Inventory0 Navigated to Audit Log from Top Row',
    payload: { numberOfComponents },
  });
};

const trackOpenComponentsInVisualization = (
  numberOfComponents: number,
  visualization: ViewIds,
  openedWithQuery: boolean
) => {
  trackEventV2({
    event:
      'Inventory0 Opened selected components in visualization from Top Row',
    payload: { visualization, numberOfComponents, openedWithQuery },
  });
};

const trackOpenOverviewViewpoint = (numberOfComponents: number) => {
  trackEventV2({
    event: 'Inventory0 Opened Overview Viewpoint from Top Row',
    payload: { numberOfComponents },
  });
};

const trackOpenApplySavedViewpointModal = (
  numberOfComponents: number,
  openedWithQuery: boolean
) => {
  trackEventV2({
    event: 'Inventory0 Opened apply saved viewpoint modal from Top Row',
    payload: { numberOfComponents, openedWithQuery },
  });
};

const trackOpenPriorityViewpoint = (
  numberOfComponents: number,
  openedWithQuery: boolean
) => {
  trackEventV2({
    event: 'Inventory0 Opened priority viewpoint from Top Row',
    payload: { numberOfComponents, openedWithQuery },
  });
};

const trackSaveAsReport = (numberOfFilters: number) => {
  trackEventV2({
    event: 'Inventory0 Clicked save as report',
    payload: { numberOfFilters },
  });
};

const trackOpenExportMenu = () => {
  trackEventV2({ event: 'Inventory0 Opened export menu from Top Row' });
};

const trackExportInventory = ({
  format,
  numberOfFilters,
  numberOfItems,
  numberOfColumns,
  hierarchyEnabled,
}: {
  format: InventoryExportFormat;
  numberOfFilters: number;
  numberOfItems: number;
  numberOfColumns: number;
  hierarchyEnabled: boolean;
}) => {
  trackEventV2({
    event: 'Inventory0 Clicked export',
    payload: {
      format,
      numberOfFilters,
      numberOfItems,
      numberOfColumns,
      hierarchyEnabled,
    },
  });
};

const trackOpenComponentDrawer = () => {
  trackEventV2({ event: 'Inventory0 Opened Component Drawer' });
};

const trackCloseComponentDrawer = () => {
  trackEventV2({ event: 'Inventory0 Closed Component Drawer' });
};

const trackSelectComponentFromComponentDrawer = () => {
  trackEventV2({
    event: 'Inventory0 Selected component from Component Drawer',
  });
};

const trackOpenViewpointFromComponentDrawer = () => {
  trackEventV2({ event: 'Inventory0 Opened viewpoint from Component Drawer' });
};

const trackDeleteComponents = (
  numberOfComponents: number,
  from: InventoryDatasourceSelectionType,
  isHierarchyEnabled: boolean
) => {
  trackEventV2({
    event: 'Inventory0 Deleted components',
    payload: { numberOfComponents, from, isHierarchyEnabled },
  });
};

const trackDeleteComponentsAndReferences = (
  numberOfComponents: number,
  numberOfReferences: number,
  isHierarchyEnabled: boolean
) => {
  trackEventV2({
    event: 'Inventory0 Deleted components and references',
    payload: { numberOfComponents, numberOfReferences, isHierarchyEnabled },
  });
};

const trackNavigateToAuditLogFromTable = (numberOfComponents: number) => {
  trackEventV2({
    event: 'Inventory0 Navigated to Audit Log from Table',
    payload: { numberOfComponents },
  });
};

const defaultColumnKeys = new Set<string>([
  'name',
  'description',
  'type',
  'typeId',
  'parent',
  'created',
  'createdBy',
  'lastModifiedBy',
  'lastUpdated',
  'component-key', // Ardoq ID column
  '_id', // Ardoq OID column
  'lucidchartDocuments',
  'rootWorkspace',
  TAGS_COLUMN,
  ...PROVENANCE_FIELD_KEYS,
]);

// do not leak custom field names
export const maskColumnName = (colId: string): string => {
  if (defaultColumnKeys.has(colId)) return colId;
  if (colId.endsWith('--outgoing') || colId.endsWith('--incoming')) {
    return 'reference';
  }
  return 'custom field';
};

const trackSetColumnVisibilityInTable = (
  colIds: string[],
  isVisible: boolean
) => {
  const columnNames = colIds.map(maskColumnName);
  trackEventV2({
    event: 'Inventory0 Set column visibility in table',
    payload: { numberOfColumns: columnNames.length, isVisible, columnNames },
  });
};

const trackPinColumnInTable = (position: string, triggeredFrom: string) => {
  trackEventV2({
    event: 'Inventory0 Pinned column in table',
    payload: { position, triggeredFrom },
  });
};

const trackUnPinColumnInTable = (triggeredFrom: string) => {
  trackEventV2({
    event: 'Inventory0 Unpinned column in table',
    payload: { triggeredFrom },
  });
};

const trackMoveColumnInTable = (toIndex: number) => {
  trackEventV2({
    event: 'Inventory0 Moved column in table',
    payload: { toIndex },
  });
};

/**
 * We also pass public field names such as 'name', 'description'. For custom fields, we pass replace name with "custom field".
 */
const trackFilterTableContents = (
  numberOfRules: number,
  fieldNames: string[]
) => {
  trackEventV2({
    event: 'Inventory0 Filtered contents in Table',
    payload: { numberOfRules, fieldNames },
  });
};

const trackNavigateToDataOverviewFromInventory = () => {
  trackEventV2({
    event: 'Inventory0 Navigated to Data Overview from Inventory',
  });
};

const trackCreateComponent = (isHierarchyEnabled: boolean) => {
  trackEventV2({
    event: 'Inventory0 Created new component',
    payload: { isHierarchyEnabled },
  });
};

const trackUpdateComponentFieldValue = (
  fieldName: string,
  fieldType: string,
  from: InventoryDatasourceSelectionType,
  isHierarchyEnabled: boolean
) => {
  trackEventV2({
    event: 'Inventory0 Updated component field value',
    payload: { fieldName, fieldType, from, isHierarchyEnabled },
  });
};

const trackMultiUpdateComponents = (
  count: number,
  source: string,
  from: InventoryDatasourceSelectionType,
  isHierarchyEnabled: boolean
) => {
  trackEventV2({
    event: 'Inventory0 Updated multiple components',
    payload: { count, source, from, isHierarchyEnabled },
  });
};

const trackDuplicateComponents = (count: number) => {
  trackEventV2({
    event: 'Inventory0 Duplicated components',
    payload: { count },
  });
};

const trackNavigateToPreviouslyOpenedDataInInventory = () => {
  trackEventV2({
    event: 'Inventory0 Navigated to previously opened data in Inventory',
  });
};

const trackOpenedInventoryDrawer = () => {
  trackEventV2({ event: 'Inventory0 Opened Inventory Drawer' });
};

const trackClosedInventoryDrawer = () => {
  trackEventV2({ event: 'Inventory0 Closed Inventory Drawer' });
};

const trackSortByColumnInDataOverview = ({
  sortOrder,
}: {
  sortOrder: SortOrder;
}) => {
  trackEventV2({
    event: 'Inventory0 Sorted by column in Data Overview',
    payload: { sortOrder },
  });
};

const trackHierarchyToggled = (
  enabled: boolean,
  datasourceSelection: InventoryDatasourceSelection
) => {
  trackEventV2({
    event: 'Inventory0 Toggled hierarchy',
    payload: {
      enabled,
      type: datasourceSelection.dataSourceType,
      count:
        inventoryDatasourceOperations.getDatasourceSelectionCount(
          datasourceSelection
        ),
    },
  });
};

const trackBulkCreateOrDeleteReferences = ({
  createdCount,
  deletedCount,
  source,
  from,
  isHierarchyEnabled,
}: {
  deletedCount: number;
  createdCount: number;
  source: ReferenceUpdateSource;
  from: InventoryDatasourceSelectionType;
  isHierarchyEnabled: boolean;
}) => {
  if (createdCount > 0) {
    trackEventV2({
      event: 'Inventory0 Created references in bulk',
      payload: { count: createdCount, source, from, isHierarchyEnabled },
    });
  }
  if (deletedCount > 0) {
    trackEventV2({
      event: 'Inventory0 Deleted references in bulk',
      payload: { count: deletedCount, source, from, isHierarchyEnabled },
    });
  }
};

const trackOpenSurveyFromComponentDrawer = () => {
  trackEventV2({ event: 'Inventory0 Opened survey from Component Drawer' });
};

const trackOpenSurvey = () => {
  trackEventV2({ event: 'Inventory0 Opened survey' });
};

const trackChildComponentAdded = () => {
  trackEventV2({ event: 'Inventory0 Added child component' });
};

const trackHierarchyDisplayed = (
  workspaceCount: number,
  type: 'workspace' | 'component type',
  areFiltersApplied: boolean
) => {
  trackEventV2({
    event: 'Inventory0 Hierarchy displayed',
    payload: {
      workspaceCount,
      type,
      areFiltersApplied,
    },
  });
};

const trackComponentParentChangedWithContextMenu = () => {
  trackEventV2({
    event: 'Inventory0 Component parent changed with context menu',
  });
};

const trackRowExpandedInHierarchy = (level: number) => {
  trackEventV2({
    event: 'Inventory0 Expanded row in hierarchy',
    payload: { level },
  });
};

const trackRowCollapsedInHierarchy = (level: number) => {
  trackEventV2({
    event: 'Inventory0 Collapsed row in hierarchy',
    payload: { level },
  });
};

const trackCollapseAllInHierarchy = () => {
  trackEventV2({
    event: 'Inventory0 Collapsed all rows in hierarchy',
  });
};

const trackExpandAllInHierarchy = () => {
  trackEventV2({
    event: 'Inventory0 Expanded all rows in hierarchy',
  });
};

const trackMultiUpdateTagsForComponents = (count: number) => {
  trackEventV2({
    event: 'Inventory0 Updated tags for multiple components',
    payload: { count },
  });
};

const trackUpdateComponentTags = ({
  addedCount,
  removedCount,
}: {
  addedCount: number;
  removedCount: number;
}) => {
  trackEventV2({
    event: 'Inventory0 Updated component tags',
    payload: { addedCount, removedCount },
  });
};

const trackTagUpdates = (pendingTagChanges: PendingTagChange[]) => {
  if (pendingTagChanges.length === 0) {
    return;
  }
  if (pendingTagChanges.length === 1) {
    const { tagsToAdd, tagsToRemove } = pendingTagChanges[0];
    trackUpdateComponentTags({
      addedCount: tagsToAdd.length,
      removedCount: tagsToRemove.length,
    });
  }
  if (pendingTagChanges.length > 1) {
    trackMultiUpdateTagsForComponents(pendingTagChanges.length);
  }
};

const BULK_EDIT_VALUE_MODES = {
  value: 'setValue',
  empty: 'clearValue',
  default: 'setToDefault',
} as const;

/**
 * One bulk edit of one field, reported by the kind of field it wrote to rather
 * than by name: custom field names are the organisation's own data.
 */
const trackRuleBasedEditField = ({
  fieldType,
  numberOfComponents,
  shouldOnlyFillEmptyCells,
  selection,
}: RuleBasedEditFieldData) => {
  trackEventV2({
    event: 'Inventory0 Bulk edited field',
    payload: {
      fieldType,
      numberOfComponents,
      shouldOnlyFillEmptyCells,
      valueMode: BULK_EDIT_VALUE_MODES[selection.mode],
    },
  });
};

const trackRuleBasedDeleteComponents = ({
  numberOfComponents,
}: RuleBasedDeleteData) => {
  trackEventV2({
    event: 'Inventory0 Bulk deleted components',
    payload: {
      numberOfComponents,
    },
  });
};

export const inventoryTracking = {
  trackOpenInventoryFromSidebar,
  trackOpenInventoryFromDiscoverLandingPage,
  trackFocusOnSearchInputInDataOverview,
  trackClearInSearchInputInDataOverview,
  trackSelectTypeInDataOverview,
  trackOpenItemsInInventory,
  trackClickOnItemNameInDataOverview,
  trackToggleCheckboxForItemInDataOverview,
  trackClickOnOpenButtonInDataOverview,
  trackOpenColumnSelectionPanelFromTopRow,
  trackEnterCreateComponentsModeFromTopRow,
  trackExitCreateComponentsModeFromTopRow,
  trackAddNewRowFromTopRow,
  trackDeselectAllRowsFromTopRow,
  trackDeleteSelectedRowsFromTopRow,
  trackNavigateToAuditLogFromTopRow,
  trackOpenComponentsInVisualization,
  trackOpenOverviewViewpoint,
  trackOpenApplySavedViewpointModal,
  trackOpenPriorityViewpoint,
  trackSaveAsReport,
  trackOpenExportMenu,
  trackExportInventory,
  trackOpenComponentDrawer,
  trackCloseComponentDrawer,
  trackSelectComponentFromComponentDrawer,
  trackOpenViewpointFromComponentDrawer,
  trackDeleteComponents,
  trackDeleteComponentsAndReferences,
  trackNavigateToAuditLogFromTable,
  trackSetColumnVisibilityInTable,
  trackPinColumnInTable,
  trackUnPinColumnInTable,
  trackMoveColumnInTable,
  trackFilterTableContents,
  trackNavigateToDataOverviewFromInventory,
  trackCreateComponent,
  trackUpdateComponentFieldValue,
  trackMultiUpdateComponents,
  trackDuplicateComponents,
  trackNavigateToPreviouslyOpenedDataInInventory,
  trackEnterCreateComponentsModeFromEmptyState,
  trackOpenInventoryFromAssetManager,
  trackOpenInventoryFromMetamodel,
  trackClipboardDataTooLarge,
  trackSortByColumnInDataOverview,
  trackOpenedInventoryDrawer,
  trackClosedInventoryDrawer,
  trackHierarchyToggled,
  trackBulkCreateOrDeleteReferences,
  trackOpenSurveyFromComponentDrawer,
  trackOpenSurvey,
  trackComponentParentChangedWithContextMenu,
  trackHierarchyDisplayed,
  trackChildComponentAdded,
  trackCollapseAllInHierarchy,
  trackExpandAllInHierarchy,
  trackRowExpandedInHierarchy,
  trackRowCollapsedInHierarchy,
  trackTagUpdates,
  trackRuleBasedEditField,
  trackRuleBasedDeleteComponents,
};
